/**
 * Settings — one destination for everything that is not the queue.
 *
 * Emails, Activity, and Settings used to be three navigation items under a
 * "System" heading, competing for attention with the five things the client
 * actually signs in to do. They are the same kind of thing — administrative,
 * occasional, never the reason anyone opened the portal — so they are one
 * destination with tabs, reached from the account menu rather than the main
 * navigation.
 *
 * Nothing was removed. Portal preferences, outgoing-email configuration, the
 * Alice framework, the Switchboard, the send log, and the full activity
 * timeline are all still here, and permissions are unchanged: every one of
 * these surfaces is enforced by the Worker, not by whether a tab is rendered.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Activity,
  BellRing,
  ChevronRight,
  Mail,
  Settings2,
  Sparkles,
  ToggleRight,
  UserRound,
} from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  ListTabs,
  usePageHeader,
} from '@command-center/ui-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../lib/api';
import {
  DEFAULT_FOLLOW_UP_OFFSET_DAYS,
  LEAD_FOLLOW_UP_SETTINGS_KEY,
  portalSettingsApi,
} from '../lib/portal-settings-api';
import { adminConfig } from '../platform/config';
import { useAuth } from '../auth/AuthProvider';
import {
  NotificationDeliveryLog,
  PortalNotificationSettings,
} from '@command-center/oneup-portal-ui';
import { notificationsClient } from '../lib/notifications-api';
import { ActivityView } from './ActivityView';
import { EmailsView } from './EmailsView';

type SettingsTab = 'portal' | 'notifications' | 'emails' | 'activity' | 'account';

const TABS: SettingsTab[] = ['portal', 'notifications', 'emails', 'activity', 'account'];

export function SettingsView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab') as SettingsTab | null;
  const tab: SettingsTab = requested && TABS.includes(requested) ? requested : 'portal';

  usePageHeader({ title: 'Settings', subtitle: 'Portal preferences, logs, and your account.' }, []);

  const select = (next: SettingsTab) => {
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        if (next === 'portal') params.delete('tab');
        else params.set('tab', next);
        // Selection inside a tab does not survive a tab change.
        params.delete('email');
        return params;
      },
      { replace: true },
    );
  };

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <ListTabs
        label="Settings"
        active={tab}
        onChange={(next) => select(next as SettingsTab)}
        tabs={[
          { id: 'portal', label: 'Portal', icon: <Settings2 className="h-4 w-4" /> },
          {
            id: 'notifications',
            label: 'Notifications',
            icon: <BellRing className="h-4 w-4" />,
          },
          { id: 'emails', label: 'Email Log', icon: <Mail className="h-4 w-4" /> },
          { id: 'activity', label: 'Activity', icon: <Activity className="h-4 w-4" /> },
          { id: 'account', label: 'Your Account', icon: <UserRound className="h-4 w-4" /> },
        ]}
      />
      {tab === 'portal' && <PortalTab />}
      {tab === 'notifications' && <NotificationsTab />}
      {tab === 'emails' && <EmailsView />}
      {tab === 'activity' && <ActivityView />}
      {tab === 'account' && <AccountTab />}
    </div>
  );
}

/* -------------------------------------------------------------- notifications */

/**
 * Which application events send an email, to whom, and what it says — plus the
 * delivery log that answers "did it actually go out". The editor itself is the
 * shared component every portal uses; only the arrangement is Cascade's.
 */
function NotificationsTab() {
  return (
    <div className="flex min-w-0 flex-col gap-6">
      <p className="max-w-3xl text-sm text-muted-foreground">
        Automatic emails for events LeadLoop already tracks. Turning one on does not change what is
        recorded in the portal — only who is emailed about it.
      </p>
      <PortalNotificationSettings client={notificationsClient} />
      <section className="flex min-w-0 flex-col gap-3">
        <h2 className="text-sm font-semibold text-foreground">Delivery log</h2>
        <NotificationDeliveryLog client={notificationsClient} />
      </section>
    </div>
  );
}

/* --------------------------------------------------------------------- portal */

function PortalTab() {
  const flags = Object.entries(adminConfig.featureFlags ?? {});
  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Portal</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-foreground">
          <p>
            {adminConfig.branding.name} · <span className="text-muted-foreground">v0.1.0</span>
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Records and activity are managed by the platform control plane. Feature flags are
            managed per install by One Up.
          </p>
        </CardContent>
      </Card>

      <LeadFollowUpSettingsCard />

      <EmailSettingsCard />

      {/* Automations and Alice are configuration surfaces, not daily work, so
          they are reached from here rather than from the main navigation. */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Automations</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-1">
          <SettingsLink
            to="/switchboard"
            icon={<ToggleRight className="h-4 w-4" aria-hidden />}
            title="Switchboard"
            description="Configure, activate, and monitor the automated workflows behind LeadLoop."
          />
          <SettingsLink
            to="/settings/alice"
            icon={<Sparkles className="h-4 w-4" aria-hidden />}
            title="Alice Framework"
            description="Tools, connections, prompts, and the policy in force for this portal."
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Feature flags</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="flex flex-col gap-1 text-sm">
            {flags.map(([flag, enabled]) => (
              <li key={flag} className="flex items-center justify-between gap-3">
                <span className="min-w-0 break-words text-foreground">{flag}</span>
                <span className={enabled ? 'shrink-0 text-green-600' : 'shrink-0 text-muted-foreground'}>
                  {enabled ? 'on' : 'off'}
                </span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

function SettingsLink({
  to,
  icon,
  title,
  description,
}: {
  to: string;
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      className="-m-2 flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-foreground">{title}</span>
        <span className="block text-xs text-muted-foreground">{description}</span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
    </Link>
  );
}

/* -------------------------------------------------------------------- account */

/**
 * The signed-in person, and the one thing they can actually change about
 * themselves. Roles, memberships, and portal access are granted centrally by
 * One Up — this portal shows them, it does not edit them.
 */
function AccountTab() {
  const { user, impersonation, updatePassword } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();

  const email = impersonation?.impersonation.targetEmail ?? user?.email ?? '—';
  const name = String(user?.user_metadata?.full_name ?? user?.user_metadata?.name ?? '') || undefined;
  const valid = password.length >= 8 && password === confirm;
  const readOnly = Boolean(impersonation);

  const save = async () => {
    setBusy(true);
    setError(undefined);
    setMessage(undefined);
    const result = await updatePassword(password);
    setBusy(false);
    if (result.ok) {
      setPassword('');
      setConfirm('');
      setMessage('Password updated.');
      window.setTimeout(() => setMessage(undefined), 3000);
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Signed in as</CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          {name && <p className="font-medium text-foreground">{name}</p>}
          <p className="break-all text-muted-foreground">{email}</p>
          <p className="mt-2 text-xs text-muted-foreground">
            Portal access and permissions are managed by One Up. Contact us to add or remove a
            user.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Change password</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            <label className="text-xs font-medium text-muted-foreground" htmlFor="new-password">
              New password
            </label>
            <Input
              id="new-password"
              className="mt-1"
              type="password"
              autoComplete="new-password"
              value={password}
              disabled={busy || readOnly}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground" htmlFor="confirm-password">
              Confirm new password
            </label>
            <Input
              id="confirm-password"
              className="mt-1"
              type="password"
              autoComplete="new-password"
              value={confirm}
              disabled={busy || readOnly}
              onChange={(event) => setConfirm(event.target.value)}
            />
          </div>
          {password.length > 0 && password.length < 8 && (
            <p className="text-xs text-muted-foreground">Use at least 8 characters.</p>
          )}
          {confirm.length > 0 && password !== confirm && (
            <p className="text-xs text-destructive">The two passwords do not match.</p>
          )}
          {error && <p className="text-xs text-destructive">{error}</p>}
          {message && <p className="text-xs text-green-600">{message}</p>}
          {/* A support session is read-only by contract; changing the customer's
              password from inside one is exactly what that guarantees against. */}
          {readOnly ? (
            <p className="text-xs text-muted-foreground">
              Not available while viewing this portal as another user.
            </p>
          ) : (
            <Button type="button" disabled={!valid || busy} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Update password'}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ---------------------------------------------------------------------- cards */

interface EmailSettings {
  fromName: string;
  fromEmail: string;
  replyTo: string;
  mailgunDomain: string;
  persisted: boolean;
}

const EMPTY_EMAIL_SETTINGS: EmailSettings = {
  fromName: '',
  fromEmail: '',
  replyTo: '',
  mailgunDomain: '',
  persisted: false,
};

function EmailSettingsCard() {
  const [settings, setSettings] = useState<EmailSettings>(EMPTY_EMAIL_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    request<{ settings: EmailSettings }>('/api/emails/settings')
      .then((response) => {
        if (!cancelled) setSettings(response.settings);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unable to load email settings.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function save() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const response = await request<{ settings: EmailSettings }>('/api/emails/settings', {
        method: 'PUT',
        body: JSON.stringify({
          fromName: settings.fromName,
          fromEmail: settings.fromEmail,
          replyTo: settings.replyTo,
        }),
      });
      setSettings(response.settings);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save email settings.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold">Outgoing email</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <p className="text-xs text-muted-foreground">
          These fields control what recipients see. The Mailgun API key and verified sending domain
          remain secured in the Worker configuration.
        </p>

        <div>
          <label className="text-xs font-medium text-muted-foreground" htmlFor="email-from-name">
            Sender name
          </label>
          <Input
            id="email-from-name"
            className="mt-1"
            value={settings.fromName}
            disabled={loading || saving}
            onChange={(event) =>
              setSettings((current) => ({ ...current, fromName: event.target.value }))
            }
            placeholder="Cascade Electric"
          />
        </div>

        <div>
          <label className="text-xs font-medium text-muted-foreground" htmlFor="email-from-address">
            From email address
          </label>
          <Input
            id="email-from-address"
            className="mt-1"
            type="email"
            value={settings.fromEmail}
            disabled={loading || saving}
            onChange={(event) =>
              setSettings((current) => ({ ...current, fromEmail: event.target.value }))
            }
            placeholder="no-reply@cascadeelectricchico.com"
          />
        </div>

        <div>
          <label className="text-xs font-medium text-muted-foreground" htmlFor="email-reply-to">
            Reply-to email address <span className="font-normal">(optional)</span>
          </label>
          <Input
            id="email-reply-to"
            className="mt-1"
            type="email"
            value={settings.replyTo}
            disabled={loading || saving}
            onChange={(event) =>
              setSettings((current) => ({ ...current, replyTo: event.target.value }))
            }
            placeholder="office@cascadeelectricchico.com"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Leave blank and replies will go to the From address.
          </p>
        </div>

        <div className="rounded-md border bg-muted/30 px-3 py-2">
          <div className="text-xs font-medium text-muted-foreground">Mailgun sending domain</div>
          <code className="mt-1 block break-all text-xs text-foreground">
            {settings.mailgunDomain || 'Not configured'}
          </code>
        </div>

        {error && <p className="text-xs text-destructive">{error}</p>}
        {saved && <p className="text-xs text-green-600">Email settings saved.</p>}

        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" disabled={loading || saving} onClick={() => void save()}>
            {saving ? 'Saving…' : 'Save email settings'}
          </Button>
          {!loading && !settings.persisted && (
            <span className="text-xs text-muted-foreground">Currently using Worker defaults.</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * The three quick offsets the Follow Up control offers on LeadLoop surfaces.
 * Three slots, because three is what the Action Center shows — this is a
 * retune, not a list editor.
 */
function LeadFollowUpSettingsCard() {
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: LEAD_FOLLOW_UP_SETTINGS_KEY,
    queryFn: portalSettingsApi.leadFollowUp,
    retry: false,
  });
  /*
    Both hops are optional. `?.` on `data` alone was not enough: an answer that
    parses as JSON but carries no `settings` key threw, and now that Settings is
    one page that crash would take the Email Log and Activity down with it.
  */
  const stored = settings.data?.settings?.snoozeDurationDays ?? DEFAULT_FOLLOW_UP_OFFSET_DAYS;
  const [draft, setDraft] = useState<string[] | null>(null);
  const [saved, setSaved] = useState(false);
  const values = draft ?? stored.map(String);

  const save = useMutation({
    mutationFn: () => portalSettingsApi.saveLeadFollowUp(values.map((value) => Number(value))),
    onSuccess: async (response) => {
      setDraft(null);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
      queryClient.setQueryData(LEAD_FOLLOW_UP_SETTINGS_KEY, response);
      await queryClient.invalidateQueries({ queryKey: LEAD_FOLLOW_UP_SETTINGS_KEY });
    },
  });

  const parsed = values.map((value) => Number(value));
  const valid =
    parsed.every((days) => Number.isInteger(days) && days >= 1 && days <= 365) &&
    new Set(parsed).size === parsed.length;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold">Lead follow-up</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <p className="text-xs text-muted-foreground">
          The three quick Follow Up options offered on a lead — each sets the next follow-up this
          many days from today. A date can always be picked directly instead.
        </p>
        <div className="grid grid-cols-3 gap-3">
          {values.map((value, index) => (
            <div key={index}>
              <label
                className="text-xs font-medium text-muted-foreground"
                htmlFor={`follow-up-option-${index}`}
              >
                Option {index + 1}
              </label>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  id={`follow-up-option-${index}`}
                  type="number"
                  min={1}
                  max={365}
                  value={value}
                  disabled={settings.isLoading || save.isPending}
                  onChange={(event) =>
                    setDraft(values.map((current, i) => (i === index ? event.target.value : current)))
                  }
                />
                <span className="shrink-0 text-xs text-muted-foreground">days</span>
              </div>
            </div>
          ))}
        </div>

        {!valid && (
          <p className="text-xs text-destructive">
            Enter three different whole numbers of days between 1 and 365.
          </p>
        )}
        {save.isError && (
          <p className="text-xs text-destructive">
            {save.error instanceof Error ? save.error.message : 'Unable to save these options.'}
          </p>
        )}
        {saved && <p className="text-xs text-green-600">Follow Up options saved.</p>}

        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            disabled={!valid || settings.isLoading || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save follow-up settings'}
          </Button>
          {!settings.isLoading && !settings.data?.settings?.persisted && (
            <span className="text-xs text-muted-foreground">Currently using the defaults.</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
